import { AuthRedirectService } from '@edtech-ui/core/services/auth-redirect/auth-redirect.service';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { LoginPayload } from '@edtech-ui/core/models/interfaces/login';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { ToastrService } from 'ngx-toastr';
import { SharedModule } from 'primeng/api';
import { Subscription } from 'rxjs';
import { normalizeTimeZone } from '@edtech-ui/core/utils/date-time.util';
import { OneSignalService } from '@edtech-ui/core/services/one-signal-service/one-signal.service';
import { FireBaseServiceService } from '@edtech-ui/core/services/firebaseService/fire-base-service.service';

@Component({
	selector: 'app-user-registration-login-with-phone-otp',
	standalone: true,
	imports: [CommonModule, SharedModule, ReactiveFormsModule, MatProgressSpinnerModule, RouterModule],
	templateUrl: './user-registration-login-with-phone-otp.component.html',
	styleUrl: './user-registration-login-with-phone-otp.component.scss',
})
export class UserRegistrationLoginWithPhoneOtpComponent implements OnInit, OnDestroy {
	@ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

	loginForm!: FormGroup;
	subscription = new Subscription();
	isLoading: boolean = false;
	userEmail: string | null = null;
	userPhone: string = '';
	resendTimer: number = 120;
	intervalId: any;
	canResendOtp: boolean = false;
	displayTime: string = '02:00';
	fcmToken: string = '';
	redirectUrl: string = '';

	constructor(
		private router: Router,
		private _toastr: ToastrService,
		private _authService: AuthServiceService,
		private _oneSignalService: OneSignalService,
		private route: ActivatedRoute,
		private _fireBaseService: FireBaseServiceService,
		private _authRedirectService: AuthRedirectService
	) {
		this.loginForm = new FormGroup({
			otp: new FormArray(Array.from({ length: 6 }, () => new FormControl('', Validators.required)))
		});
	}

	ngOnInit(): void {
		this.userEmail = this._authService.getEmail();
		this.route.queryParams.subscribe(params => {
			this.userPhone = params['phone'] || '';
			this.redirectUrl = params['redirectUrl'];
		});

		this.startResendOtpTimer();
		this.fcmToken = this._authService.getFcmToken() || '';
		setTimeout(() => this.focusFirstOtp(), 100);
	}

	get otpControls(): FormArray {
		return this.loginForm.get('otp') as FormArray;
	}

	isOtpValid(): boolean {
		return this.otpControls.controls.every(ctrl => ctrl.value);
	}

	onOtpInput(event: Event, index: number): void {
		const input = event.target as HTMLInputElement;
		const value = input.value;
		if (!/^\d$/.test(value)) { input.value = ''; return; }
		if (value && index < this.otpControls.length - 1) {
			(input.nextElementSibling as HTMLInputElement)?.focus();
		}
	}

	onOtpKeyDown(event: KeyboardEvent, index: number): void {
		if (event.key === 'Backspace' && !this.otpControls.at(index).value && index > 0) {
			((event.target as HTMLInputElement).previousElementSibling as HTMLInputElement)?.focus();
		}
	}

	onOtpPaste(event: ClipboardEvent): void {
		event.preventDefault();
		const pasted = event.clipboardData?.getData('text') || '';
		if (!/^\d{6}$/.test(pasted)) return;
		pasted.split('').forEach((digit, i) => this.otpControls.at(i).setValue(digit));
	}

	private focusFirstOtp(): void {
		this.otpInputs?.first?.nativeElement.focus();
	}

	async onLogin() {
		if (!this.userPhone) {
			this._toastr.error('Phone number not found. Please try again.');
			return;
		}

		if (!this.isOtpValid()) return;
		this.isLoading = true;

		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown';
		const language = navigator.language || '';
		const country = language.includes('-') ? language.split('-')[1] : 'Unknown';
		const usersPhone = (this.userPhone || '').toString().replace(/\s/g, '');

		this.isLoading = true;

		let fcmToken = localStorage.getItem('fcmToken') || '';
		if (!fcmToken) {
			try {
				fcmToken = (await Promise.race([
					this._fireBaseService.getFCMToken(),
					new Promise((resolve) => setTimeout(() => resolve(''), 5000))
				])) as string || '';
			} catch (e) {
			}
		}

		const loginPayload: LoginPayload = {
			phone: usersPhone,
			otp: this.otpControls.value.join(''),
			fcm_token: fcmToken,
			is_default: true,
			login_source: "web",
			user_timezone: normalizeTimeZone(timeZone),
			country: country,
			onesignal_player_id: localStorage.getItem('onesignal_player_id') || '',
		};

		this.subscription.add(
			this._authService.postLogin(loginPayload).subscribe({
				next: (response) => {
					this.isLoading = false;

					if (!response.Success) {
						this._toastr.error(response.Message || 'Login failed.');
						return;
					}

					if (response.Data?.user_id) {
						const newUserId = response.Data.user_id.toString();
						localStorage.setItem('user_id', response.Data.user_id.toString());
						this._authService.currentUserId.next(newUserId);
					}

					if (response.Data?.phone_email) {
						this._authService.currentUserEmail.next(response.Data.phone_email);
						localStorage.setItem('phone_email', response.Data.phone_email);
					}


					if (response.Data?.token) {
						this._authService.setToken(response.Data.token);
					}

					if (response.Data.refresh_token) {
						localStorage.setItem('refresh_token', response.Data.refresh_token);
					}

					if (response.Data?.modules) {
						this._authService.setModules(response.Data.modules);
					}

					const pendingRoundId = localStorage.getItem('pending_practice_round_id');
					if (pendingRoundId && (response.Data?.role_id === 2 || response.Data?.role_id === 1)) {
						localStorage.removeItem('pending_practice_round_id');
						this.router.navigate(['/student-dashboard/ai-interview-dashboard']);
					} else {
						this._authRedirectService.handlePostLoginRedirect(this.redirectUrl);
					}
				},
				error: (err) => {
					this.isLoading = false;
					this._toastr.error(err?.error?.Message);
				},
				complete: () => {
					this.isLoading = false;
				}
			})
		);
	}


	goToPhonePage() {
		this.router.navigate(['/login'], { queryParams: { phone: this.userPhone } });
	}

	startResendOtpTimer() {
		if (this.intervalId) {
			clearInterval(this.intervalId);
		}

		this.canResendOtp = false;
		this.resendTimer = 120;

		this.updateDisplayTime();

		this.intervalId = setInterval(() => {
			this.resendTimer--;

			this.updateDisplayTime();

			if (this.resendTimer <= 0) {
				clearInterval(this.intervalId);
				this.canResendOtp = true;
			}
		}, 1000);
	}

	updateDisplayTime() {
		const minutes = Math.floor(this.resendTimer / 60);
		const seconds = this.resendTimer % 60;
		this.displayTime = `${minutes < 10 ? '0' : ''}${minutes}:` + `${seconds < 10 ? '0' : ''}${seconds}`;
	}

	resendOtp() {
		if (!this.userPhone) {
			this._toastr.error('Phone number not found. Please try again.');
			return;
		}
		const usersPhone = (this.userPhone || '').toString().replace(/\s/g, '');

		this.isLoading = true;
		const loginPayload: LoginPayload = {
			phone: usersPhone,
			fcm_token: this.fcmToken,
			is_default: true,
			login_source: "web"
		};

		this.subscription.add(
			this._authService.postLogin(loginPayload).subscribe({
				next: (response) => {
					this.isLoading = false;
					if (response.Success || response.Status === 200) {
						this._toastr.success(response.Message);
						this.clearOtpFields();
						this.startResendOtpTimer();
					} else {
						this._toastr.error(response.Message);
					}
				},
				error: (err) => {
					this.isLoading = false;
					this._toastr.error(err?.error?.Message);
				}
			})
		);
	}

	clearOtpFields(): void {
		this.otpControls.controls.forEach(control => control.setValue(''));
		this.focusFirstOtp();
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		if (this.intervalId) {
			clearInterval(this.intervalId);
		}
	}
}